import { isOneOf } from './array.helpers';

/**
 * Pure upload-path rules for the project-file batch pipeline.
 * Server zip-flatten and client folder-walk share these so junk / archive
 * detection stays in one place. Caps apply after flatten, not before.
 */

const OFFICE_OPENXML_EXTENSIONS = [
  '.docx',
  '.dotx',
  '.xlsx',
  '.xlsm',
  '.xlsb',
  '.pptx',
  '.ods',
  '.odt',
  '.odp',
] as const;

const ZIP_ARCHIVE_EXTENSIONS = ['.zip'] as const;

/** CAD / BIM models — geometry import, not this extraction pipeline. */
const EXTRACTION_SKIP_EXTENSIONS = [
  '.ifc',
  '.ifcxml',
  '.ifczip',
  '.dwg',
  '.dxf',
  '.rvt',
  '.rfa',
  '.rte',
  '.nwd',
  '.nwc',
  '.skp',
  '.3dm',
  '.step',
  '.stp',
] as const;

const JUNK_BASENAMES = new Set([
  '.ds_store',
  'thumbs.db',
  'desktop.ini',
  '.localized',
]);

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsm': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv': 'text/csv',
  '.txt': 'text/plain',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.ifc': 'application/x-step',
  '.ifczip': 'application/zip',
  '.zip': 'application/zip',
};

/** Max file rows after zip/folder flatten. */
export const PROJECT_FILE_UPLOAD_MAX_FILES = 200;

/** Per-document byte cap after flatten. Not applied to the zip part itself. */
export const PROJECT_FILE_UPLOAD_MAX_MEMBER_BYTES = 50_000_000;

/** Uncompressed total after flatten, and multer cap for one raw part (the zip). */
export const PROJECT_FILE_UPLOAD_MAX_TOTAL_BYTES = 200_000_000;

/** Nested zip depth, counting the uploaded archive as 1. */
export const PROJECT_FILE_UPLOAD_MAX_ZIP_DEPTH = 3;

/**
 * Last path-segment extension including the dot, lowercased.
 * No extension → empty string.
 */
export const projectFileUploadExtension = (fileName: string): string => {
  const base = fileName.replace(/\\/g, '/').split('/').pop() ?? fileName;
  const i = base.lastIndexOf('.');
  if (i <= 0) return '';
  return base.slice(i).toLowerCase();
};

/**
 * True when the name is a zip container we should unpack.
 * Office Open XML stays packed — those are zip-under-the-hood documents.
 */
export const isProjectFileZipArchiveName = (fileName: string): boolean => {
  const ext = projectFileUploadExtension(fileName);
  if (isOneOf(OFFICE_OPENXML_EXTENSIONS, ext)) return false;
  return isOneOf(ZIP_ARCHIVE_EXTENSIONS, ext);
};

/**
 * True for CAD/BIM files this pipeline cannot classify (e.g. `.ifc`).
 * Skip them during flatten so one model does not fail a zip of PDFs.
 */
export const isProjectFileExtractionSkipName = (fileName: string): boolean =>
  isOneOf(EXTRACTION_SKIP_EXTENSIONS, projectFileUploadExtension(fileName));

/**
 * Ground truth that rides along in an FFU folder. Not pipeline input.
 * Matches `facit.json`, `facit.proposed.json`, `facit.json.bak`, and any
 * other `facit.*` basename, in any folder.
 */
export const isProjectFileFacitName = (fileName: string): boolean => {
  const base =
    fileName.replace(/\\/g, '/').split('/').pop()?.toLowerCase() ?? '';
  return base.startsWith('facit.');
};

/**
 * Skip facit sidecars, CAD names, and oversize documents.
 * Zip archives are unpacked first, then their members are skipped — except an
 * archive whose own name is `facit.*`.
 */
export const shouldSkipProjectFileFlattenMember = (
  fileName: string,
  sizeBytes: number,
): boolean => {
  if (isProjectFileFacitName(fileName)) return true;
  if (isProjectFileZipArchiveName(fileName)) return false;
  if (isProjectFileExtractionSkipName(fileName)) return true;
  if (sizeBytes > PROJECT_FILE_UPLOAD_MAX_MEMBER_BYTES) return true;
  return false;
};

/**
 * MIME inferred from the filename. Unknown extensions keep `fallback`.
 */
export const mimeTypeFromProjectFileUploadName = (
  fileName: string,
  fallback = 'application/octet-stream',
): string =>
  MIME_BY_EXTENSION[projectFileUploadExtension(fileName)] ?? fallback;

/**
 * Undo a UTF-8 filename that arrived as Latin-1 code units, then compose
 * macOS NFD (`a` + ring) into `å`. Already-Unicode names are left intact
 * aside from composition.
 */
export const normalizeProjectFileUploadText = (value: string): string => {
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code > 0xff) return value.normalize('NFC');
    bytes[i] = code;
  }
  let repaired: string;
  try {
    repaired = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return value.normalize('NFC');
  }
  const back = new TextEncoder().encode(repaired);
  if (back.length !== value.length) return value.normalize('NFC');
  for (let i = 0; i < back.length; i++) {
    if (back[i] !== value.charCodeAt(i)) return value.normalize('NFC');
  }
  return repaired.normalize('NFC');
};

/**
 * Strip `.` / `..`, collapse slashes, drop a leading slash.
 * Empty after sanitize → empty string (caller should skip).
 */
export const sanitizeProjectFileUploadPath = (raw: string): string => {
  const parts = normalizeProjectFileUploadText(raw)
    .replace(/\\/g, '/')
    .split('/')
    .filter((p) => p !== '' && p !== '.' && p !== '..');
  return parts.join('/');
};

/**
 * Join parent + child into a sanitized relative path.
 */
export const joinProjectFileUploadPath = (
  parent: string,
  child: string,
): string => {
  const childPath = sanitizeProjectFileUploadPath(child);
  if (!parent) return childPath;
  const parentPath = sanitizeProjectFileUploadPath(parent);
  if (!parentPath) return childPath;
  if (!childPath) return parentPath;
  return `${parentPath}/${childPath}`;
};

/**
 * Archive path with the zip extension stripped, for prefixing inner members.
 * `Hus1/docs.zip` → `Hus1/docs`.
 */
export const projectFileUploadArchiveStem = (fileName: string): string => {
  const path = sanitizeProjectFileUploadPath(fileName);
  const lower = path.toLowerCase();
  if (lower.endsWith('.ifczip')) return path.slice(0, -'.ifczip'.length);
  if (lower.endsWith('.zip')) return path.slice(0, -'.zip'.length);
  return path;
};

/**
 * True for macOS/Windows junk and AppleDouble (`._*`) files, including
 * anything under `__MACOSX`.
 */
export const isProjectFileArchiveJunkPath = (path: string): boolean => {
  const normalized = sanitizeProjectFileUploadPath(path);
  if (!normalized) return true;
  const parts = normalized.split('/');
  if (parts.some((p) => p.toLowerCase() === '__macosx')) return true;
  const base = parts[parts.length - 1]?.toLowerCase() ?? '';
  if (JUNK_BASENAMES.has(base)) return true;
  if (base.startsWith('._')) return true;
  return false;
};
